import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { EVENT_FIND_CANDIDATES_WIDGET_DISPLAYED } from 'src/app/shared/dataAnalytics/schema/constants';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { useIsApplyViaSeek } from 'src/app/v1/views/hooks/useIsApplyViaSeek';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import { NotificationSettingsPanel } from '../NotificationSettingsPanel';
import { RecommendedCandidatesPanel } from '../RecommendedCandidatesPanel';
import { ShowcaseYourBrandPanel } from '../ShowcaseYourBrandPanel';

import translations from './.vocab';

interface Props {
  jobId?: string;
}
export const ExtraTaskSteps = ({ jobId }: Props) => {
  const { t } = useTranslations(translations);
  const { brandId, flowType, legacyId, hirerAccount } = useSelector(
    (state: AppState) => ({
      brandId: state.formData.brand?.id,
      flowType: state.formData.flowType,
      legacyId: state.formData.brand?.legacyId,
      hirerAccount: state.session.hirerAccount,
    }),
    shallowEqual,
  );

  const applyViaSeek = useIsApplyViaSeek();

  const displayNotificationPreferences =
    flowType !== 'refresh' && flowType !== 'edit' && applyViaSeek;

  const showBranding = Boolean(brandId || legacyId);

  const showRecommendedCandidates = Boolean(
    jobId && hirerAccount.isTalentSearchApproved && hirerAccount.isApproved,
  );

  const sendTrackingData = () => {
    const eventData: TrackingEvent = {
      eventName: EVENT_FIND_CANDIDATES_WIDGET_DISPLAYED,
    };

    getTrackingAdapter.sendLinkEvent(eventData);
  };

  useEffect(() => {
    if (showRecommendedCandidates) {
      sendTrackingData();
    }
  }, [showRecommendedCandidates]);

  return (
    <Stack space="large">
      <Box paddingX={['medium', 'medium', 'none']}>
        <Heading level="2">{t('extra-task-steps-heading')}</Heading>
      </Box>
      <Stack space="large">
        {displayNotificationPreferences && (
          <NotificationSettingsPanel jobId={jobId} />
        )}
        {showRecommendedCandidates && <RecommendedCandidatesPanel />}
        {showBranding && (
          <Stack space="medium">
            <ShowcaseYourBrandPanel />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
